import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Row,
  Col,
  Empty,
  InputNumber,
  Form,
  Tag,
  DatePicker,
  Spin
} from 'antd';
import {
  DownloadOutlined,
  LoadingOutlined,
  FileTextOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  getStatusFromRecord,
  STATUS_CONFIG
} from '@/features/drumLabel/components/DrumAllocation/DrumAllocation.helper';
import useDrumAccess from '@/features/drumLabel/hooks/useDrumAccess';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
import {
  createDrumLabel,
  getDrumLabels,
  regenerateMissingDrumPdfs,
  generateUsageLogReport
} from '@/features/drumLabel/redux/slice';
import styles from './index.module.less';

// Build a storage-service URL that streams the PDF by blob name, instead of
// hitting the raw Azure URL (which needs auth / is blocked by CORS). Matches
// the pattern used in DrumAllocation and samplingList/printSamplingPdf.
const buildBlobPdfUrl = (rawUrl) => {
  if (!rawUrl) return '';
  const filename = String(rawUrl).split(/[?#]/)[0].split('/').pop();
  if (!filename) return '';
  return `${process.env.REACT_APP_STORAGE_URL}${filename}`;
};

const DrumLabelGeneration = () => {
  const dispatch = useDispatch();
  const { list, listPagination, loading, pendingCount, stalePendingCount } =
    useSelector((state) => state.drumLabel);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [createForm] = Form.useForm();

  const { canAct } = useDrumAccess();

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logReportForm] = Form.useForm();
  const [fromDateValue, setFromDateValue] = useState(null);
  const [reportGenerating, setReportGenerating] = useState(false);

  const pollRef = useRef(null);
  const fetchListRef = useRef(null);

  useEffect(() => {
    fetchList();
  }, [page, perPage, search]);

  // Auto-poll every 5s while the backend still has pending PDFs, so new rows
  // appear without the user needing to refresh. Stops once pendingCount hits 0.
  useEffect(() => {
    if (pendingCount > 0) {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => fetchListRef.current(), 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current && pendingCount === 0) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingCount]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const fetchList = () => {
    const payload = { page, per_page: perPage };
    if (search) payload.search = search;
    dispatch(getDrumLabels(payload));
  };
  fetchListRef.current = fetchList;

  const openCreate = () => {
    createForm.setFieldsValue({ count: 1 });
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (submitting) return;
    setCreateOpen(false);
  };

  const handleLogReport = async (values) => {
    setReportGenerating(true);
    try {
      const from_date = values.from_date.format('YYYY-MM-DD');
      const to_date = values.to_date.format('YYYY-MM-DD');
      const res = await dispatch(
        generateUsageLogReport({ from_date, to_date, flat: true })
      );
      if (res?.payload?.success) {
        setLogModalOpen(false);
        logReportForm.resetFields();
        setFromDateValue(null);
      }
    } finally {
      setReportGenerating(false);
    }
  };

  const handleCreate = async ({ count }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await dispatch(
        createDrumLabel({ count: Number(count) || 1 })
      );
      if (res?.payload?.success) {
        setCreateOpen(false);
        setPage(1);
        fetchList();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerateMissing = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      await dispatch(regenerateMissingDrumPdfs());
      fetchList();
    } finally {
      setRegenerating(false);
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, idx) => (page - 1) * perPage + idx + 1
    },
    {
      title: 'DRUM Label Number',
      dataIndex: 'drum_label_number',
      key: 'drum_label_number',
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span>
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const s = getStatusFromRecord(record);
        const cfg = STATUS_CONFIG[s];
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      }
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (v) => v || '—'
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (v) =>
        v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
    },
    {
      title: 'Label',
      dataIndex: 'pdf_url',
      key: 'pdf_url',
      width: 100,
      render: (v, record) => {
        if (v) {
          return (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              href={buildBlobPdfUrl(v)}
              target="_blank"
              rel="noopener noreferrer"
              download={`${record.drum_label_number}.pdf`}
              style={{ padding: 0 }}
            >
              PDF
            </Button>
          );
        }
        const isStale = new Date() - new Date(record.createdAt) > 90 * 1000;
        return isStale ? (
          <WarningOutlined
            style={{ color: '#faad14', cursor: 'pointer' }}
            title="PDF stalled — click to retry"
            onClick={handleRegenerateMissing}
          />
        ) : (
          <Spin size="small" />
        );
      }
    }
  ];

  return (
    <Layout sider={false}>
      <div className={styles.page_wrapper}>
        <div className={styles.page_header}>
          <div className={styles.title_block}>
            <h2 className={styles.page_title}>DRUM Label Generation</h2>
            {pendingCount > 0 && (
              <span
                className={
                  stalePendingCount > 0
                    ? styles.pending_chip_warn
                    : styles.pending_chip
                }
                title={
                  stalePendingCount > 0
                    ? 'One or more labels may have stalled'
                    : 'Labels are generating in the background'
                }
              >
                {stalePendingCount > 0 ? (
                  <WarningOutlined />
                ) : (
                  <LoadingOutlined spin />
                )}{' '}
                {pendingCount}{' '}
                {stalePendingCount > 0 ? 'may have stalled' : 'generating'}
                {stalePendingCount > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={handleRegenerateMissing}
                    loading={regenerating}
                    disabled={regenerating}
                    className={styles.pending_retry}
                  >
                    Retry
                  </Button>
                )}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<FileTextOutlined />}
              disabled={!canAct}
              onClick={() => {
                logReportForm.resetFields();
                setFromDateValue(null);
                setLogModalOpen(true);
              }}
            >
              Activity Log
            </Button>
            <Button
              type="primary"
              onClick={openCreate}
              disabled={submitting || !canAct}
            >
              Create DRUM Label
            </Button>
          </div>
        </div>

        <Row gutter={16} className={styles.search_row}>
          <Col xs={24} md={10} lg={8}>
            <Input.Search
              placeholder="Search by DRUM label number"
              allowClear
              onSearch={(v) => {
                setPage(1);
                setSearch(v);
              }}
            />
          </Col>
        </Row>

        <CustomTable
          columns={columns}
          data={list}
          rowKey={(r) => r.id}
          loading={loading}
          locale={{
            emptyText: (
              <Empty description="No DRUM labels found. Create one to get started." />
            )
          }}
          pagination={{
            total: listPagination?.total_records || 0,
            pageSize: perPage,
            current: page,
            onChange: (p, ps) => {
              setPage(p);
              setPerPage(ps);
            }
          }}
          showPaginationMeta
        />

        <CustomModal
          title="Create DRUM Labels"
          visible={createOpen}
          onCancel={closeCreate}
          customFooter={false}
          footer={null}
          width={400}
        >
          <Form
            form={createForm}
            layout="vertical"
            initialValues={{ count: 1 }}
            onFinish={handleCreate}
          >
            <Form.Item
              label="Number of labels to create"
              name="count"
              rules={[
                { required: true, message: 'Required' },
                {
                  type: 'number',
                  min: 1,
                  max: 100,
                  message: 'Enter a number between 1 and 100'
                }
              ]}
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: '100%' }}
                placeholder="e.g. 5"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeCreate} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
              >
                Create
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Activity Log Report modal */}
        <CustomModal
          title="DRUM Activity Log Report"
          visible={logModalOpen}
          onCancel={() => {
            if (!reportGenerating) {
              setLogModalOpen(false);
              logReportForm.resetFields();
              setFromDateValue(null);
            }
          }}
          customFooter={false}
          footer={null}
          width={440}
        >
          <Form
            form={logReportForm}
            layout="vertical"
            onFinish={handleLogReport}
          >
            <Form.Item
              label="From Date"
              name="from_date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                onChange={(v) => setFromDateValue(v)}
              />
            </Form.Item>
            <Form.Item
              label="To Date"
              name="to_date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) =>
                  fromDateValue && d.isBefore(fromDateValue, 'day')
                }
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setLogModalOpen(false);
                  logReportForm.resetFields();
                  setFromDateValue(null);
                }}
                disabled={reportGenerating}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={reportGenerating}
                disabled={reportGenerating}
                style={{ marginLeft: 8 }}
              >
                Generate PDF
              </Button>
            </div>
          </Form>
        </CustomModal>
      </div>
    </Layout>
  );
};

export default DrumLabelGeneration;
